import type { MessageData, PaginatedResponse, SuccessResponse } from "@shared/types";
import apiClient from "@/app/api/client";
import { organizationEndpoints, replaceParams } from "@/app/api/endpoints";
import type {
	CreateOrganizationRequest,
	ListOrganizationsParams,
	Organization,
	UpdateOrganizationRequest,
} from "../types";

export interface OrganizationTreeNode {
	id: string;
	name: string;
	hasChildren: boolean;
	children?: OrganizationTreeNode[];
}

export interface OrgCameraStats {
	organizationId: string;
	nvrTotal: number;
	nvrOnline: number;
	cameraTotal: number;
	cameraOnline: number;
}

const childrenEndpoint = "/api/organization/:id/children";
const treeEndpoint = "/api/organization/tree";
const treeChildrenEndpoint = "/api/organization/:id/tree-children";

export const organizationService = {
	async list(params: ListOrganizationsParams): Promise<PaginatedResponse<Organization>> {
		const response = await apiClient.get<PaginatedResponse<Organization>>(
			organizationEndpoints.list,
			{ params }
		);
		return response.data;
	},

	async getById(id: string): Promise<Organization> {
		const url = replaceParams(organizationEndpoints.getById, { id });
		const response = await apiClient.get<SuccessResponse<Organization>>(url);
		return response.data.data;
	},

	async listChildren(
		id: string,
		params?: { page?: number; limit?: number }
	): Promise<PaginatedResponse<Organization>> {
		const url = replaceParams(childrenEndpoint, { id });
		const response = await apiClient.get<PaginatedResponse<Organization>>(url, { params });
		return response.data;
	},

	async listMyChildren(params?: {
		page?: number;
		limit?: number;
		search?: string;
		isEducational?: boolean;
	}): Promise<PaginatedResponse<Organization>> {
		const response = await apiClient.get<PaginatedResponse<Organization>>(
			"/api/organization/children",
			{ params }
		);
		return response.data;
	},

	async getCameraStats(organizationIds: string[]): Promise<OrgCameraStats[]> {
		if (organizationIds.length === 0) {
			return [];
		}
		// Backend bitta so'rovda 100  tagacha ID qabul qiladi; drill-down children
		// cheklanmagan (katta tumanlar) bo'lishi mumkin — bo'laklarga bo'lib yuboramiz.
		const CHUNK_SIZE = 100;
		const chunks: string[][] = [];
		for (let i = 0; i < organizationIds.length; i += CHUNK_SIZE) {
			chunks.push(organizationIds.slice(i, i + CHUNK_SIZE));
		}
		const results = await Promise.all(
			chunks.map(async (ids) => {
				const response = await apiClient.post<SuccessResponse<{ stats: OrgCameraStats[] }>>(
					"/api/organization/camera-stats",
					{ organizationIds: ids }
				);
				return response.data.data.stats;
			})
		);
		return results.flat();
	},

	async create(data: CreateOrganizationRequest): Promise<Organization> {
		const response = await apiClient.post<SuccessResponse<Organization>>(
			organizationEndpoints.create,
			data
		);
		return response.data.data;
	},

	async update(id: string, data: UpdateOrganizationRequest): Promise<Organization> {
		const url = replaceParams(organizationEndpoints.update, { id });
		const response = await apiClient.patch<SuccessResponse<Organization>>(url, data);
		return response.data.data;
	},

	async delete(id: string): Promise<void> {
		const url = replaceParams(organizationEndpoints.delete, { id });
		await apiClient.delete<SuccessResponse<MessageData>>(url);
	},

	async getTree(rootId?: string): Promise<SuccessResponse<OrganizationTreeNode>> {
		const response = await apiClient.get<SuccessResponse<OrganizationTreeNode>>(treeEndpoint, {
			params: rootId ? { rootId } : undefined,
		});
		return response.data;
	},

	async getTreeChildren(id: string): Promise<
		SuccessResponse<{
			items: OrganizationTreeNode[];
			meta: { total: number; hasMore: boolean };
		}>
	> {
		const url = replaceParams(treeChildrenEndpoint, { id });
		const response =
			await apiClient.get<
				SuccessResponse<{
					items: OrganizationTreeNode[];
					meta: { total: number; hasMore: boolean };
				}>
			>(url);
		return response.data;
	},
};
